import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DangerousGoodItem, LanguageService, TransportMode, minValidator } from '@hazmatsuite-next/ui';
import { Store } from '@ngrx/store';
import { map, Observable, take, tap } from 'rxjs';
import { initializeSinglePackage, loadPackagingTypes, loadUnPackagingTypes }
  from '../../../../store/package-list.actions';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SinglePackagingModel, PackagingModel, PackagingType, EnumPackageType, EasyDgdService, Packages } from '@hazmatsuite-next/easydgd-lib';

@UntilDestroy()
@Component({
  selector: 'easydgd-single-packaging-item-editor',
  templateUrl: './single-packaging-item-editor.component.html',
  styleUrls: ['./single-packaging-item-editor.component.scss'],
})
export class SinglePackagingItemEditorComponent implements OnInit {
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Output() valid = new EventEmitter<boolean>();
  @Output() packageChanged = new EventEmitter<SinglePackagingModel>();

  model: SinglePackagingModel = new SinglePackagingModel;
  formGroup?: FormGroup;
  packageType: EnumPackageType = EnumPackageType.NotSet;

  public packageTypes$ = this.store.select(state => {
    this.packageType = state.packageList.tempPackage.PackageType;
    if (state.packageList.tempPackage.PackageType === EnumPackageType.Tank) {
      return state.packageList.packagingTypesTank;
    }
    if (state.packageList.tempPackage.PackageType === EnumPackageType.Bulk) {
      return state.packageList.packagingTypesBulk;
    }
    return state.packageList.packagingTypes;
  });
  public packagingUnCodes$ = this.store.select(state => state.packageList.packagingUnCodes).pipe(
    tap(result => {
      this.unPackageTypes = result;
      if (this.model?.UseAlternativePackagingType || !this.unPackageTypes.length) {
        this.packagingUnCode?.setValidators([]);
      } else {
        this.packagingUnCode?.setValidators(Validators.required);
      }
      this.packagingUnCode?.updateValueAndValidity();
    })
  );

  public package$ = this.store.select(state => state.packageList.tempPackage).pipe(
    map((tempPackage) => {
      this.model = tempPackage as SinglePackagingModel;

      const pkg = tempPackage as SinglePackagingModel;
      this.properShippingName?.setValue(pkg.DangerousGood?.DefaultShippingExpression, { onlySelf: true });
      this.useAlternativeProperShippingName?.setValue(pkg.UseAlternativeProperShippingName, { onlySelf: true });
      this.technicalName?.setValue(pkg.TechnicalName, { onlySelf: true });
      if (pkg.IsTechnicalNameRequired) {
        this.technicalName?.enable();
      }
      this.isEnvironmentallyHazardous?.setValue(pkg.IsEnvironmentallyHazardous || pkg.IsEnvironmentallyHazardousFromDb, { onlySelf: true });
      this.countOfPackagings?.setValue(pkg.CountOfPackagings === 0 ? 1 : pkg.CountOfPackagings, { onlySelf: true });
      if (this.packagingType?.value !== pkg.PackagingType) {
        this.packagingType?.setValue(pkg.PackagingType, { onlySelf: true });
      }
      if (this.packagingUnCode?.value !== pkg.PackagingUnCode) {
        this.packagingUnCode?.setValue(pkg.PackagingUnCode, { onlySelf: true });
      }
      this.useAlternativePackagingType?.setValue(pkg.UseAlternativePackagingType, { onlySelf: true });
      this.alternativePackagingType?.setValue(pkg.AlternativePackagingType, { onlySelf: true });
      this.netWeight?.setValue(pkg.NetWeightValue, { onlySelf: true });
      this.netVolume?.setValue(pkg.NetVolumeValue, { onlySelf: true });
      this.grossWeight?.setValue(pkg.GrossWeightValue, { onlySelf: true });
      this.netExplosiveMass?.setValue(pkg.NetExplosiveMassValue, { onlySelf: true });
      this.isTotalQuantity?.setValue(pkg?.IsTotalQuantity || false, { onlySelf: true });
      return pkg;
    })
  );

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource']));

  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  private unPackageTypes: PackagingType[] = [];

  constructor(
    private store: Store<{ packageList: { packagingTypesLoaded: boolean, packages: Packages, tempPackage: PackagingModel, packagingTypes: PackagingType[], packagingTypesBulk: PackagingType[], packagingTypesTank: PackagingType[], packagingUnCodes: PackagingType[] } }>,
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService
  ) {
    this.store.select(state => state.packageList.packagingTypesLoaded).pipe(
      untilDestroyed(this)
    ).subscribe(loaded => {
      if (!loaded) {
        this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.SinglePacking, transportMode: this.easyDgdService.getTransportMode() }));
        this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.Tank, transportMode: this.easyDgdService.getTransportMode() }));
        this.store.dispatch(loadPackagingTypes({ packageType: EnumPackageType.Bulk, transportMode: this.easyDgdService.getTransportMode() }));
      }
    });
  }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      properShippingName: new FormControl({ value: '', disabled: true }),
      alternativeProperShippingName: new FormControl('', (ctrl: AbstractControl) =>
        this.model?.UseAlternativeProperShippingName ? Validators.required(ctrl) : null),
      useAlternativeProperShippingName: new FormControl(false),
      technicalName: new FormControl({ value: '', disabled: true }, (ctrl: AbstractControl) =>
        this.model?.DangerousGood?.IsTechnicalNameRequired ? Validators.required(ctrl) : null),
      isEnvironmentallyHazardous: new FormControl({ value: false, disabled: this.model?.IsEnvironmentallyHazardousFromDb ?? false }),
      lowerFlashpoint: new FormControl('', (ctrl: AbstractControl) =>
        this.model?.IsFlashpointRequired ? Validators.required(ctrl) : null),
      countOfPackagings: new FormControl(1, [Validators.required, Validators.min(1)]),
      packagingType: new FormControl('1A1', (ctrl: AbstractControl) =>
        this.model?.UseAlternativePackagingType ? null : Validators.required(ctrl)
      ),
      useAlternativePackagingType: new FormControl(false),
      packagingUnCode: new FormControl(null, (ctrl: AbstractControl) =>
        Validators.required(ctrl)
      ),
      alternativePackagingType: new FormControl('', (ctrl: AbstractControl) =>
        this.model?.UseAlternativePackagingType ? Validators.required(ctrl) : null
      ),
      isTotalQuantity: new FormControl(false, Validators.required),
      netWeight: new FormControl(null, [(ctrl: AbstractControl) =>
        this.model?.IsNetWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetWeightMandatory ? minValidator()(ctrl) : null]
      ),
      netVolume: new FormControl(null, [(ctrl: AbstractControl) =>
        this.model?.IsNetVolumeMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetVolumeMandatory ? minValidator()(ctrl) : null]
      ),
      grossWeight: new FormControl(null, [(ctrl: AbstractControl) =>
        this.model?.IsGrossWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsGrossWeightMandatory ? minValidator()(ctrl) : null]
      ),
      netExplosiveMass: new FormControl(null, [(ctrl: AbstractControl) =>
        this.model?.IsNetExplosiveMassMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetExplosiveMassMandatory ? minValidator()(ctrl) : null]
      ),
    });

    this.packagingType?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(val => {
      this.packagingUnCode?.setValue(undefined);
      this.store.dispatch(loadUnPackagingTypes({ packagingTypeId: val, transportMode: this.easyDgdService.getTransportMode(), packageType: EnumPackageType.SinglePacking }));
    });
  }

  public selectDangerousGood(dangerousGood: DangerousGoodItem): void {
    this.store.select(state => state.packageList).pipe(
      take(1)
    ).subscribe((state) => {
      const singlePackage = { ...state.tempPackage as SinglePackagingModel };
      singlePackage.DangerousGood = dangerousGood;
      singlePackage.IsEnvironmentallyHazardous = dangerousGood.IsEnvironmentallyHazardous;
      singlePackage.IsTechnicalNameRequired = dangerousGood.IsTechnicalNameRequired;
      // singlePackage.PackagingUnCode = state.tempPackage.PackagingUnCode; // dangerousGood.UnNumber

      this.store.dispatch(initializeSinglePackage({
        dangerousGood: dangerousGood,
        packageType: this.packageType,
        transportMode: this.transportMode
      }));
    });
  }

  public markAsTouched(): void {
    this.formGroup?.markAllAsTouched();
  }

  public formValueChange(singlePackageModel: SinglePackagingModel): void {
    this.valid.next(this.formGroup?.valid || false);
    this.packageChanged.emit(singlePackageModel);
  }

  get properShippingName(): AbstractControl | null | undefined {
    return this.formGroup?.get('properShippingName');
  }
  get useAlternativeProperShippingName(): AbstractControl | null | undefined {
    return this.formGroup?.get('useAlternativeProperShippingName');
  }
  get technicalName(): AbstractControl | null | undefined {
    return this.formGroup?.get('technicalName');
  }
  get isEnvironmentallyHazardous(): AbstractControl | null | undefined {
    return this.formGroup?.get('isEnvironmentallyHazardous');
  }
  get lowerFlashpoint(): AbstractControl | null | undefined {
    return this.formGroup?.get('lowerFlashpoint');
  }
  get countOfPackagings(): AbstractControl | null | undefined {
    return this.formGroup?.get('countOfPackagings');
  }
  get packagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('packagingType');
  }
  get useAlternativePackagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('useAlternativePackagingType');
  }
  get packagingUnCode(): AbstractControl | null | undefined {
    return this.formGroup?.get('packagingUnCode');
  }
  get alternativePackagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('alternativePackagingType');
  }
  get netWeight(): AbstractControl | null | undefined {
    return this.formGroup?.get('netWeight');
  }
  get netVolume(): AbstractControl | null | undefined {
    return this.formGroup?.get('netVolume');
  }
  get grossWeight(): AbstractControl | null | undefined {
    return this.formGroup?.get('grossWeight');
  }
  get netExplosiveMass(): AbstractControl | null | undefined {
    return this.formGroup?.get('netExplosiveMass');
  }
  get isTotalQuantity(): AbstractControl | null | undefined {
    return this.formGroup?.get('isTotalQuantity');
  }
}
